import { requestBlob, requestJson } from "services/http";

// Radar lab: stepped water-level lab runs in the greenhouse tank, served by
// the field-node gateway under /api/padihat/rtct/lab/. The one call outside
// it is the emergency pump stop, the greenhouse controller's own pump-stop
// endpoint (the same as Stop on the Experiment page).
const base = "/api/padihat/rtct/lab/";
const json = (method, body) => ({
  method,
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(body),
});
const CAPTURE_MODES = ["stream", "burst", "all"];
const captureMode = (mode) => (CAPTURE_MODES.includes(mode) ? mode : "all");
const bounded = (value, low, high, fallback) => {
  const n = Math.round(Number(value));
  return Number.isFinite(n) ? Math.max(low, Math.min(high, n)) : fallback;
};
// A lab run is addressed by its numeric id only, so a stray value can never
// reach another path.
const runPath = (id, resource = "") => {
  const text = String(id ?? "");
  if (!/^[0-9]+$/.test(text)) throw new Error("Invalid lab run id");
  return `${base}runs/${text}/${resource ? `${resource}/` : ""}`;
};
// A bad id rejects like a failed request instead of throwing at the caller.
const attempt = (call) => {
  try {
    return call();
  } catch (error) {
    return Promise.reject(error);
  }
};
const query = (params) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "")
      search.set(key, String(value));
  });
  const text = search.toString();
  return text ? `?${text}` : "";
};
const filenameOf = (contentDisposition, fallback) => {
  const encoded = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  const plain = contentDisposition.match(/filename="?([^";]+)"?/i);
  const name = encoded
    ? decodeURIComponent(encoded[1])
    : plain
      ? plain[1]
      : fallback;
  return (name || fallback).replace(/[^A-Za-z0-9._-]/g, "_");
};

export const radarLabApi = {
  // Everything the page needs before a lab run: the node and controller
  // choices, the checks, the derived tank level, budgets, tiers and presets.
  status: ({ node, controller } = {}, signal) =>
    requestJson(`${base}status/${query({ node, controller })}`, { signal }),
  runs: ({ limit = 20, offset = 0 } = {}, signal) =>
    requestJson(
      `${base}runs/?limit=${bounded(limit, 1, 100, 20)}&offset=${bounded(offset, 0, 1e6, 0)}`,
      { signal },
    ),
  run: (id, signal) => attempt(() => requestJson(runPath(id), { signal })),
  // A target preset takes `part` (1 or 2, for a sweep that runs in two
  // parts) and, for a zone, `band` [low, high] in cm; the server works its
  // steps out from the level when it starts.
  start: ({
    preset,
    node,
    controller,
    rulerLevelCm = null,
    notes = "",
    part = null,
    band = null,
  }) => {
    const body = { preset, node, controller };
    if (rulerLevelCm !== null && rulerLevelCm !== undefined)
      body.ruler_level_cm = rulerLevelCm;
    if (notes) body.notes = notes;
    if (part === 1 || part === 2) body.part = part;
    if (Array.isArray(band) && band.length === 2) body.band = band.map(Number);
    return requestJson(`${base}runs/`, json("POST", body));
  },
  stop: (id) =>
    attempt(() => requestJson(runPath(id, "stop"), json("POST", {}))),
  setLevel: (id, levelCm) =>
    attempt(() =>
      requestJson(runPath(id, "level"), json("POST", { level_cm: levelCm })),
    ),
  readings: (id, { since } = {}, signal) =>
    attempt(() =>
      requestJson(`${runPath(id, "readings")}${query({ since })}`, { signal }),
    ),
  captures: (id, { since, limit = 100, mode = "all" } = {}, signal) =>
    attempt(() =>
      requestJson(
        `${runPath(id, "captures")}${query({
          since,
          limit: bounded(limit, 1, 300, 100),
          mode: captureMode(mode),
        })}`,
        { signal },
      ),
    ),
  // The ruler camera's readings against the node over the last `days` (1-31,
  // default 7): each confident reading paired with the node's air gap, the
  // mapping fitted to each estimator and their errors.
  validation: ({ node, controller, days = 7 } = {}, signal) =>
    requestJson(
      `${base}validation/${query({ node, controller, days: bounded(days, 1, 31, 7) })}`,
      { signal },
    ),
  // The lab run's dataset as one zip, saved through a throwaway link.
  exportRun: async (id) => {
    const { blob, contentDisposition } = await requestBlob(
      runPath(id, "export"),
    );
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filenameOf(contentDisposition, `lab-run-${id}.zip`);
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  },
  // The greenhouse controller's pump stop (main backend): stops a running
  // pump at once and cancels any pump operation still waiting for its photo.
  emergencyPumpStop: (controllerSlug) => {
    const slug = String(controllerSlug || "")
      .replace(/:/g, "")
      .toUpperCase();
    if (!/^[0-9A-F]{12}$/.test(slug))
      return Promise.reject(new Error("Invalid controller address"));
    return requestJson(
      `/api/padihat/greenhouse/devices/${slug}/pump-stop/`,
      json("POST", {}),
    );
  },
};
